import { Language } from '@mui/icons-material';
import { useRef } from 'react';
import type { FieldErrors, FieldValues, Path, UseFormRegister, UseFormWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { MultilangTextModal } from 'components/modals/MultilangTextModal';
import { useModal } from 'utils/hooks';
import { Languages } from 'types/translator';
import { useTranslation } from 'react-i18next';

interface MultilangTextInputProps<T extends FieldValues> {
  register: UseFormRegister<T>;
  registerName: Path<T>;
  label?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  inputClassName?: string;
  fields?: {
    label: string;
    name: string;
    required: boolean;
    placeholder: string;
    type: string;
  }[];
  watch: UseFormWatch<T>;
  errors?: FieldErrors<T>;
  error?: FieldValues['errors'];
}

export const MultilangTextInput = <T extends FieldValues>({
  label,
  register,
  className,
  registerName,
  disabled,
  watch,
  inputClassName,
  placeholder,
  fields,
  errors,
  error,
}: MultilangTextInputProps<T>) => {
  const { i18n, t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const nameValues = watch(['name_cs', 'name_en', 'name_sk'] as Path<T>[]);

  const languageMap: Record<string, number> = {
    [Languages.CS]: 0,
    [Languages.EN]: 1,
    [Languages.SK]: 2,
  };

  const valueByLang = nameValues[languageMap[i18n.language]] || '';

  const { trigger: triggerTableModal } = useModal({
    component: MultilangTextModal,
    key: 'addMultilangText',
    defaultValues: {
      fields,
      register: register as UseFormRegister<FieldValues>,
      title: t('core.modules.name'),
      errors,
    },
  });

  return (
    <div className={`flex flex-col ${className}`}>
      {label && (
        <label htmlFor={registerName} className="mb-1 text-sm">
          {label}
        </label>
      )}
      <div
        onClick={triggerTableModal}
        className={`flex items-center pl-2 py-1 rounded-md focus-within::outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent 
                ${error ? 'border-red-500' : ''}
                ${disabled ? 'bg-gray-200' : 'bg-transparent border-1 border-primary'}
                `}>
        <input
          type="text"
          id={registerName}
          className={twMerge(`w-full bg-transparent focus:outline-none p-2 h-full ${inputClassName}`)}
          disabled={disabled}
          placeholder={placeholder}
          ref={(e) => {
            inputRef.current = e;
          }}
          value={valueByLang}
          onChange={() => {}}
        />
        <Language className="mr-3 cursor-pointer text-primary" />
      </div>
      {error && <span className="text-xs text-red-500">{error.message}</span>}
    </div>
  );
};
